import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import { useDisconnectIntegration } from '@/features/integrations/hooks';
import { QuickbooksPageShell } from './QuickbooksPageShell';

/**
 * Public landing page Intuit redirects users to when they click
 * "Disconnect" on the Atlas listing inside QuickBooks Online's My Apps
 * page.
 *
 * Registered with Intuit as the **Disconnect URL**. Must be reachable
 * without Atlas authentication so the Intuit reviewer can fetch it.
 *
 * Behavior:
 *   - If the user is signed in to Atlas, we eagerly mark their
 *     `integrations` row as `disconnected` so the Atlas UI matches
 *     the state QuickBooks has just adopted. Cron-driven syncs will
 *     stop on the next tick. (Intuit also revokes the refresh token
 *     server-side on their end, so any future qbo-sync would 401
 *     anyway.)
 *   - If the user isn't signed in we still show the confirmation
 *     screen so the Intuit-redirected user has a clear endpoint, and
 *     we trust the next authenticated sync to surface a "needs
 *     reconnect" status.
 */
export function QuickbooksDisconnectPage() {
  const { session, profile, loading } = useAuth();
  const disconnect = useDisconnectIntegration();
  const ranRef = useRef(false);
  const [serverState, setServerState] = useState<'idle' | 'updated' | 'failed'>(
    'idle',
  );

  useEffect(() => {
    if (loading || !session || !profile) return;
    if (!canRead(profile.role, 'integrations', profile.section_access)) return;
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        await disconnect.mutateAsync('quickbooks');
        setServerState('updated');
      } catch {
        setServerState('failed');
      }
    })();
  }, [loading, session, profile, disconnect]);

  return (
    <QuickbooksPageShell
      eyebrow="QuickBooks Online · Disconnect"
      title="QuickBooks has been disconnected"
      description={
        <p>
          Atlas will stop syncing data from your QuickBooks Online
          account. Your historical chart of accounts, Profit &amp; Loss,
          and balance sheet snapshots remain in Atlas as read-only
          records — nothing is deleted. You can reconnect at any time
          to resume the 15-minute sync.
        </p>
      }
    >
      <div className="rounded-lg border border-border bg-card p-5 text-sm">
        <p className="font-medium text-foreground">What happens now</p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-muted-foreground">
          <li>
            The scheduled QuickBooks sync stops. Atlas will not pull any new
            chart-of-accounts, Profit &amp; Loss, or balance sheet data
            until you reconnect.
          </li>
          <li>
            Atlas immediately discards the QuickBooks refresh token from
            its encrypted storage. Intuit also revokes its copy of the
            token on their side.
          </li>
          <li>
            Other Atlas integrations (banking, marketplace channels) are
            unaffected and continue to run on their own schedules.
          </li>
        </ul>
      </div>

      {serverState === 'updated' && (
        <p className="text-xs text-old-growth">
          Atlas integration record updated to <strong>disconnected</strong>.
        </p>
      )}
      {serverState === 'failed' && (
        <p className="text-xs text-fresh-salmon-dark">
          Couldn&rsquo;t update the Atlas integration record automatically.
          A super admin can finalize the disconnect from the{' '}
          <Link to="/integrations" className="underline underline-offset-4">
            Integrations
          </Link>{' '}
          page.
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/quickbooks/connect">Reconnect QuickBooks</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard">Return to Atlas</Link>
        </Button>
      </div>
    </QuickbooksPageShell>
  );
}
